import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Camera, Plus, Trash2, Save, ArrowLeft, Image as ImageIcon,
  AlertCircle, Loader, GripVertical, X, Check, Search
} from "lucide-react";
import { api } from "../lib/api";

export default function VideoGalleryManager() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [video, setVideo] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  
  // Media gallery state
  const [allPictures, setAllPictures] = useState([]);
  const [filteredPictures, setFilteredPictures] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [selectedPictures, setSelectedPictures] = useState(new Set());

  useEffect(() => {
    loadGallery();
    loadMediaGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  useEffect(() => {
    if (searchQuery.trim()) {
      setFilteredPictures(
        allPictures.filter(pic => 
          pic.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          pic.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          pic.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
        )
      );
    } else {
      setFilteredPictures(allPictures);
    }
  }, [searchQuery, allPictures]);

  const loadGallery = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/admin/videos/${videoId}/gallery`);
      setVideo({ id: response.data.video_id, title: response.data.title });
      setGallery(response.data.gallery_photos || []);
    } catch (error) {
      console.error("Failed to load gallery:", error);
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  const loadMediaGallery = async () => {
    try {
      const response = await api.get("/api/pictures/");
      const pictures = response.data || [];
      
      // Filter to show pictures linked to this video OR pictures with no linked video
      const availablePictures = pictures.filter(pic => 
        !pic.linked_video_id || pic.linked_video_id === videoId
      );
      
      setAllPictures(availablePictures);
      setFilteredPictures(availablePictures);
    } catch (error) {
      console.error("Failed to load media gallery:", error);
      toast.error("Failed to load media gallery");
    }
  };

  const handleAddSelectedPhotos = async () => {
    if (selectedPictures.size === 0) {
      toast.error("Please select at least one photo");
      return;
    }

    try {
      const selectedPics = allPictures.filter(pic => selectedPictures.has(pic.id));
      const newPhotos = selectedPics.map(pic => pic.image_url).filter(url => !gallery.includes(url));
      
      if (newPhotos.length === 0) {
        toast.info("Selected photos are already in gallery");
        return;
      }

      const updatedGallery = [...gallery, ...newPhotos];
      
      const response = await api.put(`/api/admin/videos/${videoId}/gallery`, {
        gallery_photos: updatedGallery
      });
      
      if (response.data.ok) {
        setGallery(response.data.gallery_photos);
        setSelectedPictures(new Set());
        setShowMediaGallery(false);
        toast.success(`Added ${newPhotos.length} photo(s) to gallery`);
      }
    } catch (error) {
      console.error("Failed to add photos:", error);
      toast.error("Failed to add photos");
    }
  };

  const handleRemovePhoto = async (index) => {
    try {
      const response = await api.delete(`/api/admin/videos/${videoId}/gallery/${index}`);
      
      if (response.data.ok) {
        setGallery(response.data.gallery_photos);
        toast.success("Photo removed");
      }
    } catch (error) {
      console.error("Failed to remove photo:", error);
      toast.error("Failed to remove photo");
    }
  };

  const handleSaveOrder = async () => {
    try {
      setSaving(true);
      const response = await api.put(`/api/admin/videos/${videoId}/gallery`, {
        gallery_photos: gallery
      });
      
      if (response.data.ok) {
        toast.success("Gallery order saved");
      }
    } catch (error) {
      console.error("Failed to save gallery:", error);
      toast.error("Failed to save gallery");
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newGallery = [...gallery];
    const draggedItem = newGallery[draggedIndex];
    newGallery.splice(draggedIndex, 1);
    newGallery.splice(index, 0, draggedItem);
    
    setGallery(newGallery);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const togglePictureSelection = (pictureId) => {
    const newSelected = new Set(selectedPictures);
    if (newSelected.has(pictureId)) {
      newSelected.delete(pictureId);
    } else {
      newSelected.add(pictureId);
    }
    setSelectedPictures(newSelected);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader size={48} className="text-[#FF10F0] animate-spin" />
          <p className="text-zinc-400">Loading gallery...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-zinc-400 hover:text-[#FF10F0] transition-colors"
              >
                <ArrowLeft size={20} />
                <span className="text-sm font-medium">Back</span>
              </button>
              <div>
                <h1 className="text-xl font-bold">Gallery Manager</h1>
                <p className="text-sm text-zinc-500">{video?.title}</p>
              </div>
            </div>
            
            <button
              onClick={handleSaveOrder}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-[#FF10F0] to-[#FF2D95] text-white font-bold rounded-lg hover:shadow-[0_0_20px_rgba(255,16,240,0.4)] transition-all disabled:opacity-50"
            >
              {saving ? <Loader size={18} className="animate-spin" /> : <Save size={18} />}
              {saving ? "Saving..." : "Save Order"}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        {/* Add From Media Gallery */}
        <div className="mb-8 p-6 bg-[#0A0A0E] border border-[#FF10F0]/20 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF10F0] to-[#FF2D95] flex items-center justify-center">
                <Plus size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Add From Media Gallery</h2>
                <p className="text-sm text-zinc-500">Select photos from your existing media collection</p>
              </div>
            </div>
            
            <button
              onClick={() => setShowMediaGallery(!showMediaGallery)}
              className="px-6 py-3 bg-gradient-to-r from-[#FF10F0] to-[#FF2D95] text-white font-bold rounded-xl hover:shadow-[0_0_20px_rgba(255,16,240,0.4)] transition-all"
            >
              {showMediaGallery ? "Hide" : "Browse"} Media Gallery
            </button>
          </div>
          
          {showMediaGallery && (
            <div className="mt-6 space-y-4">
              {/* Search */}
              <div className="relative">
                <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search photos by title, description, or tags..."
                  className="w-full pl-12 pr-4 py-3 bg-black border border-white/10 focus:border-[#FF10F0]/40 rounded-xl text-white placeholder-zinc-600 outline-none transition-colors"
                />
              </div>

              {/* Selection Actions */}
              {selectedPictures.size > 0 && (
                <div className="flex items-center justify-between p-4 bg-[#FF10F0]/10 border border-[#FF10F0]/30 rounded-xl">
                  <span className="text-sm text-white">
                    {selectedPictures.size} photo(s) selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedPictures(new Set())}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleAddSelectedPhotos}
                      className="px-6 py-2 bg-gradient-to-r from-[#FF10F0] to-[#FF2D95] text-white font-bold rounded-lg hover:shadow-[0_0_15px_rgba(255,16,240,0.4)] transition-all"
                    >
                      Add to Gallery
                    </button>
                  </div>
                </div>
              )}

              {/* Photos Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto p-2">
                {filteredPictures.map((picture) => {
                  const isSelected = selectedPictures.has(picture.id);
                  const isInGallery = gallery.includes(picture.image_url);
                  
                  return (
                    <div
                      key={picture.id}
                      onClick={() => !isInGallery && togglePictureSelection(picture.id)}
                      className={`relative group rounded-xl overflow-hidden cursor-pointer transition-all ${
                        isInGallery
                          ? "opacity-40 cursor-not-allowed"
                          : isSelected
                          ? "ring-2 ring-[#FF10F0] shadow-[0_0_20px_rgba(255,16,240,0.3)]"
                          : "hover:ring-2 hover:ring-[#FF10F0]/50"
                      }`}
                    >
                      <div className="aspect-square relative">
                        <img
                          src={picture.thumbnail_url || picture.image_url}
                          alt={picture.title}
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Selection Checkbox */}
                        {!isInGallery && (
                          <div className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            isSelected
                              ? "bg-[#FF10F0] border-[#FF10F0]"
                              : "bg-black/50 border-white/50"
                          }`}>
                            {isSelected && <Check size={14} className="text-white" />}
                          </div>
                        )}
                        
                        {/* Already in Gallery Badge */}
                        {isInGallery && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="text-xs text-white font-bold">Already Added</span>
                          </div>
                        )}
                      </div>
                      
                      {picture.title && (
                        <div className="p-2 bg-[#0A0A0E]">
                          <p className="text-xs text-zinc-400 truncate">{picture.title}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {filteredPictures.length === 0 && (
                <div className="p-12 text-center">
                  <ImageIcon size={48} className="text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">No photos found</p>
                </div>
              )}
            </div>
          )}
          
          <div className="mt-4 p-3 bg-[#FF10F0]/10 border border-[#FF10F0]/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-[#FF10F0] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-zinc-400">
                Photos from your Media Gallery that are linked to this video or unlinked photos are available. Drag to reorder after adding.
              </p>
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        {gallery.length > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Camera size={24} className="text-[#FF10F0]" />
                Gallery Photos ({gallery.length})
              </h2>
              <p className="text-sm text-zinc-500">Drag to reorder</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gallery.map((photo, index) => (
                <div
                  key={index}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`group relative rounded-xl overflow-hidden bg-[#0A0A0E] border-2 transition-all cursor-move ${
                    draggedIndex === index
                      ? "border-[#FF10F0] shadow-[0_0_20px_rgba(255,16,240,0.3)]"
                      : "border-white/10 hover:border-[#FF10F0]/40"
                  }`}
                >
                  <div className="aspect-video relative">
                    <img
                      src={photo}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Drag Handle */}
                    <div className="absolute top-2 left-2 w-8 h-8 rounded-lg bg-black/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical size={16} className="text-white" />
                    </div>
                    
                    {/* Index Badge */}
                    <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-[#FF10F0] flex items-center justify-center text-white text-sm font-bold shadow-lg">
                      {index + 1}
                    </div>
                    
                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemovePhoto(index)}
                      className="absolute bottom-2 right-2 w-8 h-8 rounded-lg bg-red-500/90 hover:bg-red-500 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 size={16} className="text-white" />
                    </button>
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  
                  <div className="p-3 border-t border-white/10">
                    <p className="text-xs text-zinc-500 truncate">{photo}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-12 bg-[#0A0A0E] border border-white/10 rounded-2xl text-center">
            <ImageIcon size={64} className="text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-zinc-400 mb-2">No photos in gallery</h3>
            <p className="text-sm text-zinc-600 mb-4">Browse your Media Gallery above to add photos</p>
          </div>
        )}
      </div>
    </div>
  );
}
